import { ImportMapper } from "@/components/PImport/types/ImportMapper";
import { TableColumnData } from "@/components/PImport/types/TableColumnData";
import { DataType } from "@/types/enums/DataType";
import { Guest } from "@/types/Guest";
import { parseGuest } from "./parser";

/**
 * Contact-row allowlist for the CRM import (CONTACT_IMPORT_CREATE). The 15
 * core Contact fields + additionalFields — matches the mappable subset of
 * FIELDS_CONTACT (createdAt/updatedAt are server-minted and excluded from the
 * mapper anyway) and the backend contract: the producer mints _ids and
 * computes emailNormalized itself, so rows carry ONLY these keys.
 */
const CONTACT_ROW_ALLOWED_KEYS = [
  "title",
  "gender",
  "firstName",
  "lastName",
  "email",
  "phone",
  "mobile",
  "company",
  "jobTitle",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  "additionalFields",
] as const;

/**
 * Contact twin of {@link exportGuests}: same mapping/parsing pipeline (the
 * mapper's fields come from the company FieldsProvider, whose built-ins are
 * FIELDS_CONTACT ⊆ FIELDS_GUEST), then each row is shaped to the contact
 * import wire contract by stripping every non-contact key.
 */
export function exportContacts(
  mappingMap: Map<number, ImportMapper>,
  totalLength: number,
  tableData: TableColumnData[]
): Record<string, unknown>[] {
  return exportGuests(mappingMap, totalLength, tableData).map((guest) => {
    const source = guest as unknown as Record<string, unknown>;
    const row: Record<string, unknown> = {};
    for (const key of CONTACT_ROW_ALLOWED_KEYS) {
      if (source[key] !== undefined) row[key] = source[key];
    }
    return row;
  });
}

export function exportGuests(
  mappingMap: Map<number, ImportMapper>,
  totalLength: number,
  tableData: TableColumnData[]
): Guest[] {
  const guests: Guest[] = [];

  const relevantMapperIndexes = Array.from(mappingMap.entries())
    .filter(([, mapper]) => mapper.field.dataType !== DataType.UNKNOWN)
    .map(([index]) => index);

  for (let i = 0; i < totalLength; i++) {
    const guest = {} as Guest;

    relevantMapperIndexes.map((index) => {
      const relevantValue =
        index < tableData?.length ? tableData[index].values[i] : null;
      const relevantMappingEntry = mappingMap.get(index);
      if (!relevantMappingEntry) {
        return;
      }

      parseGuest(
        guest,
        relevantMappingEntry.field,
        relevantValue ?? "",
        relevantMappingEntry.value
      );
    });
    guests.push(guest);
  }
  return guests;
}
